import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';

// InternalNotesPanel — admin/ops-only notes attached to an account, separate
// from interactions (timeline). Markdown content is rendered as plain text in
// Sprint B; rich rendering ships when the editor lands. Per item 1.15a, these
// notes are NEVER returned by /api/client/*.

export default function InternalNotesPanel({ accountId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafting, setDrafting] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get(`/api/admin/accounts/${accountId}/internal-notes`);
      setNotes(res.notes || []);
    } catch (err) {
      setError(err?.message || 'Failed to load notes');
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { if (accountId) refresh(); }, [accountId, refresh]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!drafting.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/api/admin/accounts/${accountId}/internal-notes`, { body: drafting.trim() });
      setDrafting('');
      refresh();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to add note');
    } finally { setSubmitting(false); }
  };

  const onSaveEdit = async () => {
    if (!editingId || !editBody.trim()) return;
    setSubmitting(true);
    try {
      await api.put(`/api/admin/accounts/${accountId}/internal-notes?note_id=${encodeURIComponent(editingId)}`, { body: editBody.trim() });
      setEditingId(null);
      setEditBody('');
      refresh();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this internal note?')) return;
    try {
      await api.delete(`/api/admin/accounts/${accountId}/internal-notes?note_id=${encodeURIComponent(id)}`);
      refresh();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-3" data-testid="internal-notes-panel">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">Internal notes (admin/ops only, never shown to clients)</div>
      <form onSubmit={onAdd} className="space-y-2">
        <textarea
          rows={3}
          value={drafting}
          onChange={(e) => setDrafting(e.target.value)}
          placeholder="Type a note…"
          className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600"
          data-testid="internal-notes-draft"
        />
        <div className="flex justify-end">
          <button type="submit" disabled={submitting || !drafting.trim()}
            data-testid="internal-notes-add"
            className="px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold disabled:opacity-50">
            Add note
          </button>
        </div>
      </form>
      {error && <div className="text-[12px] text-red-300">{error}</div>}
      {loading && notes.length === 0 ? (
        <div className="animate-pulse h-12 rounded-lg bg-white/[0.03]" />
      ) : notes.length === 0 ? (
        <div className="text-[12px] text-gray-500" data-testid="internal-notes-empty">No internal notes yet.</div>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]" data-testid={`internal-note-${n.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-[11px] text-gray-500">
                  {n.authorName || 'Unknown'} · {String(n.createdAt || '').slice(0, 16)}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditingId(n.id); setEditBody(n.body); }}
                    className="p-1 rounded text-gray-500 hover:text-white" aria-label="Edit">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => onDelete(n.id)} aria-label="Delete"
                    data-testid={`internal-note-delete-${n.id}`}
                    className="p-1 rounded text-gray-500 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              {editingId === n.id ? (
                <div className="space-y-2">
                  <textarea rows={3} value={editBody} onChange={(e) => setEditBody(e.target.value)}
                    className="w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[13px] text-white" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setEditingId(null); setEditBody(''); }}
                      className="text-[11px] text-gray-400 hover:text-white">Cancel</button>
                    <button onClick={onSaveEdit} disabled={submitting || !editBody.trim()}
                      className="px-2 py-1 rounded-full bg-[#B0FC38] text-gray-900 text-[11px] disabled:opacity-50">
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <pre className="text-[13px] text-gray-200 whitespace-pre-wrap font-sans">{n.body}</pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
